import React from 'react';
import { themes, pageTypeMeta } from './themes';
import { BookOpen, Star, List, Trophy, Quote, BarChart3 } from 'lucide-react';

const ReadingPage = ({ page, pageIndex, totalPages, theme }) => {
  const t = themes[theme] || themes['modern-minimal'];
  const meta = pageTypeMeta[page.pageType] || { label: 'Reading', color: t.accent };

  const renderBookLog = () => {
    const bookFields = page.bookFields || ['Title', 'Author', 'Genre', 'Start date', 'End date', 'Rating (1-5)', 'Review', 'Favorite quote'];
    return (
      <div className="flex-1 overflow-hidden">
        <div className="space-y-1.5">
          {bookFields.map((field, fi) => {
            const isLong = field.toLowerCase().includes('review') || field.toLowerCase().includes('quote') || field.toLowerCase().includes('anmeldelse') || field.toLowerCase().includes('sitat');
            return (
              <div key={fi}>
                <span style={{ fontFamily: t.bodyFont, fontSize: '0.55rem', fontWeight: 600, color: t.accent, textTransform: 'uppercase', letterSpacing: '0.08em' }}>{field}</span>
                <div className={`mt-0.5 rounded-lg ${isLong ? 'h-8' : 'h-5'}`} style={{ backgroundColor: t.fieldBg || t.bg, border: `1px solid ${t.fieldBorder || t.accent + '15'}` }} />
              </div>
            );
          })}
        </div>
        {/* Star rating visual */}
        <div className="flex items-center gap-1 mt-2">
          {[1, 2, 3, 4, 5].map(s => (
            <Star key={s} className="w-3.5 h-3.5" style={{ color: t.accent + '30' }} strokeWidth={1.5} />
          ))}
        </div>
      </div>
    );
  };

  const renderChallenge = () => {
    const items = page.challengeItems || page.fields || [];
    return (
      <div className="flex-1 overflow-hidden">
        {page.content && <p className="mb-2" style={{ fontFamily: t.bodyFont, fontSize: '0.6rem', fontStyle: 'italic', color: t.subtext }}>{page.content}</p>}
        <div className="space-y-1">
          {items.map((item, i) => (
            <div key={i} className="flex items-center gap-2 py-0.5">
              <div className="w-4 h-4 rounded border flex-shrink-0 flex items-center justify-center" style={{ borderColor: t.accent + '40' }}>
                <span style={{ fontFamily: t.bodyFont, fontSize: '0.45rem', fontWeight: 700, color: t.accent + '40' }}>{i + 1}</span>
              </div>
              <span style={{ fontFamily: t.bodyFont, fontSize: '0.55rem', color: t.text }}>{item}</span>
              <div className="flex-1 h-4 rounded" style={{ borderBottom: `1px dashed ${t.accent}20` }} />
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderQuotes = () => (
    <div className="flex-1 flex flex-col">
      {page.content && <p className="mb-2" style={{ fontFamily: t.bodyFont, fontSize: '0.6rem', fontStyle: 'italic', color: t.subtext }}>{page.content}</p>}
      <div className="space-y-3 flex-1">
        {[0, 1, 2].map(i => (
          <div key={i} className="rounded-xl p-3" style={{ backgroundColor: t.accentLight || t.accent + '08' }}>
            <Quote className="w-3 h-3 mb-1" style={{ color: t.accent + '40' }} />
            <div className="space-y-2">
              <div className="h-px w-full" style={{ backgroundColor: t.accent + '15' }} />
              <div className="h-px w-full" style={{ backgroundColor: t.accent + '15' }} />
            </div>
            <div className="flex items-center gap-1.5 mt-2">
              <span style={{ fontFamily: t.bodyFont, fontSize: '0.5rem', color: t.subtext }}>— Book:</span>
              <div className="flex-1 h-3 rounded" style={{ borderBottom: `1px dashed ${t.accent}20` }} />
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  const renderReadingList = () => (
    <div className="flex-1 overflow-hidden">
      {page.content && <p className="mb-2" style={{ fontFamily: t.bodyFont, fontSize: '0.6rem', fontStyle: 'italic', color: t.subtext }}>{page.content}</p>}
      <div className="space-y-1">
        {(page.fields || Array.from({ length: 10 }, (_, i) => `Book ${i + 1}`)).map((f, fi) => (
          <div key={fi} className="flex items-center gap-2">
            <div className="w-3 h-3 rounded border flex-shrink-0" style={{ borderColor: t.accent + '40' }} />
            <div className="flex-1 h-5 rounded" style={{ backgroundColor: t.fieldBg || t.bg, border: `1px solid ${t.fieldBorder || t.accent + '10'}` }} />
          </div>
        ))}
      </div>
    </div>
  );

  const renderDefault = () => (
    <div className="flex-1 overflow-hidden">
      {page.content && <p className="mb-2 leading-relaxed" style={{ fontFamily: t.bodyFont, fontSize: '0.65rem', color: t.subtext, lineHeight: 1.6 }}>{page.content}</p>}
      {page.fields && page.fields.length > 0 && (
        <div className="space-y-1.5">
          {page.fields.map((f, fi) => (
            <div key={fi} className="flex items-center gap-2">
              <div className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: t.accent }} />
              <span style={{ fontFamily: t.bodyFont, fontSize: '0.6rem', color: t.text, fontWeight: 500 }}>{f}</span>
              <div className="flex-1 h-6 rounded-lg" style={{ backgroundColor: t.fieldBg || t.bg, border: `1px solid ${t.fieldBorder || t.accent + '15'}` }} />
            </div>
          ))}
        </div>
      )}
    </div>
  );

  const contentMap = {
    'reading-log': renderBookLog,
    'reading-challenge': renderChallenge,
    'reading-quotes': renderQuotes,
    'reading-list': renderReadingList,
    'reading-recommendations': renderReadingList,
  };

  const render = contentMap[page.pageType] || renderDefault;

  return (
    <div data-testid={`document-page-${pageIndex}`} className="w-full aspect-[210/297] flex flex-col relative overflow-hidden" style={{ backgroundColor: t.cardBg }}>
      <div className="h-1 w-full" style={{ backgroundColor: meta.color + '40' }} />
      <div className="px-7 pt-4 pb-2 flex items-start justify-between">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-2">
            <BookOpen className="w-3 h-3" style={{ color: meta.color }} />
            <span className="text-[8px] font-bold tracking-[0.15em] uppercase" style={{ fontFamily: t.bodyFont, color: meta.color }}>{meta.label}</span>
          </div>
          <h2 style={{ fontFamily: t.font, fontWeight: 700, fontSize: 'clamp(1rem, 2.5vw, 1.3rem)', color: t.text, lineHeight: 1.2 }}>{page.title}</h2>
        </div>
        <span style={{ fontFamily: t.bodyFont, fontSize: '0.6rem', color: t.subtext, opacity: 0.5 }}>{pageIndex + 1}/{totalPages}</span>
      </div>
      <div className="px-7 flex-1 overflow-hidden flex flex-col">{render()}</div>
      <div className="px-7 py-2 flex items-center justify-between" style={{ borderTop: `1px solid ${meta.color}10` }}>
        <span style={{ fontFamily: t.bodyFont, fontSize: '0.55rem', color: t.subtext, opacity: 0.4, letterSpacing: '0.1em' }}>LUMERA STUDIO</span>
        <span style={{ fontFamily: t.bodyFont, fontSize: '0.55rem', color: t.subtext, opacity: 0.4 }}>Page {pageIndex + 1}</span>
      </div>
    </div>
  );
};

export default ReadingPage;
